import React, { useState, useEffect } from "react";
import "./App.css";
import {
  calculatePricing,
  calculateRequiredSeats,
  DEPLOYMENT_MODELS,
  PROFESSIONAL_SERVICES,
  BUNDLE_PER_SEAT,
  SUPPORT_TIERS,
} from "./utils/pricingEngine";
import PricingBreakdown from "./components/PricingBreakdown";
import PasswordGate from "./components/PasswordGate";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [inputMode, setInputMode] = useState("seats"); // 'usage' or 'seats'
  const [inputs, setInputs] = useState({
    deploymentModel: "SHARED_SAAS",
    manualSeats: 75,
    usage: {
      inputTokens: 37.5, // millions (75 seats × 0.5M)
      outputTokens: 11.25, // millions (75 seats × 0.15M)
      meetingHours: 375, // (75 seats × 5 hours)
      transcriptionHours: 375,
    },
    integrations: {
      connections: 15,
      records: 1000000,
      actions: 10000,
    },
    contractYears: 1,
    discount: 0,
    supportTier: "ENTERPRISE",
    professionalServices: {
      hours: 0,
      rate: PROFESSIONAL_SERVICES.standardRate,
    },
    setupFeeWaived: false,
  });

  const [results, setResults] = useState(null);

  // Check authentication on mount
  useEffect(() => {
    const authenticated = sessionStorage.getItem('tectra_authenticated');
    if (authenticated === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Calculate required seats in real-time for display
  const requiredSeats = calculateRequiredSeats(inputs.usage);
  const displaySeats = inputs.manualSeats || requiredSeats;

  const handleInputChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUsageChange = (field, value) => {
    setInputMode("usage");
    setInputs((prev) => ({
      ...prev,
      manualSeats: null, // Clear manual seats when usage changes
      usage: {
        ...prev.usage,
        [field]: parseFloat(value) || 0,
      },
    }));
  };

  const handleSeatsChange = (seats) => {
    setInputMode("seats");
    const seatCount = parseInt(seats) || 0;
    setInputs((prev) => ({
      ...prev,
      manualSeats: seatCount,
      usage: {
        inputTokens: seatCount * BUNDLE_PER_SEAT.inputTokens,
        outputTokens: seatCount * BUNDLE_PER_SEAT.outputTokens,
        meetingHours: seatCount * BUNDLE_PER_SEAT.meetingHours,
        transcriptionHours: seatCount * BUNDLE_PER_SEAT.transcriptionHours,
      },
    }));
  };

  const handleIntegrationChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      integrations: {
        ...prev.integrations,
        [field]: parseFloat(value) || 0,
      },
    }));
  };

  const handleProfessionalServicesChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      professionalServices: {
        ...prev.professionalServices,
        [field]: parseFloat(value) || 0,
      },
    }));
  };

  const handleCalculate = () => {
    const pricing = calculatePricing(inputs);
    setResults(pricing);
  };

  const model = DEPLOYMENT_MODELS[inputs.deploymentModel];

  // Show password gate if not authenticated
  if (!isAuthenticated) {
    return <PasswordGate onAuthenticated={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="App">
      <div className="container">
        <header className="header">
          <img src="/icon.svg" alt="Tectra" className="logo" />
          <h1>Tectra Pricing Calculator</h1>
        </header>

        <div className="calculator-layout">
          <div className="inputs-section">
            <div className="card">
              <h2>Deployment Model</h2>
              <div className="form-group">
                <label>
                  <input
                    type="radio"
                    name="deploymentModel"
                    value="SHARED_SAAS"
                    checked={inputs.deploymentModel === "SHARED_SAAS"}
                    onChange={(e) =>
                      handleInputChange("deploymentModel", e.target.value)
                    }
                  />
                  <span className="radio-label">
                    <strong>Shared SaaS</strong> - $
                    {DEPLOYMENT_MODELS.SHARED_SAAS.pricePerSeat}/seat/month
                  </span>
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="radio"
                    name="deploymentModel"
                    value="DEDICATED_CLOUD"
                    checked={inputs.deploymentModel === "DEDICATED_CLOUD"}
                    onChange={(e) =>
                      handleInputChange("deploymentModel", e.target.value)
                    }
                  />
                  <span className="radio-label">
                    <strong>Dedicated Cloud</strong> - $
                    {DEPLOYMENT_MODELS.DEDICATED_CLOUD.pricePerSeat}/seat/month
                    <span className="hint">(50 seat minimum)</span>
                  </span>
                </label>
              </div>
            </div>

            <div className="card">
              <h2>Contract Term</h2>
              <div className="form-group">
                <label htmlFor="contractYears">Contract Length</label>
                <select
                  id="contractYears"
                  value={inputs.contractYears}
                  onChange={(e) =>
                    handleInputChange("contractYears", parseInt(e.target.value))
                  }
                  className="input-field"
                >
                  <option value="1">1 Year (0% discount)</option>
                  <option value="2">2 Years (5% discount)</option>
                  <option value="3">3 Years (10% discount)</option>
                </select>
              </div>
            </div>

            <div className="card">
              <h2>Seats & Usage Calculator</h2>
              <p className="hint">
                Enter either seats OR usage - they will sync automatically. Each
                seat includes: {BUNDLE_PER_SEAT.inputTokens}M input,{" "}
                {BUNDLE_PER_SEAT.outputTokens}M output tokens,{" "}
                {BUNDLE_PER_SEAT.meetingHours} meeting hours (pooled)
              </p>

              <div className="usage-calculator-display">
                <div className="calculator-mode-toggle">
                  <button
                    className={`mode-btn ${
                      inputMode === "seats" ? "active" : ""
                    }`}
                    onClick={() => setInputMode("seats")}
                  >
                    Calculate by Seats
                  </button>
                  <button
                    className={`mode-btn ${
                      inputMode === "usage" ? "active" : ""
                    }`}
                    onClick={() => setInputMode("usage")}
                  >
                    Calculate by Usage
                  </button>
                </div>
                <div className="required-seats-badge">
                  {inputMode === "usage" ? "Required Seats" : "Total Seats"}:{" "}
                  <strong>{displaySeats}</strong>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="manualSeats">Number of Seats</label>
                <input
                  id="manualSeats"
                  type="number"
                  min="1"
                  value={displaySeats}
                  onChange={(e) => handleSeatsChange(e.target.value)}
                  className="input-field"
                />
                <p className="hint">
                  {inputMode === "usage"
                    ? "Auto-calculated from usage below"
                    : "Usage estimates will update based on seats"}
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="inputTokens">Input Tokens (millions)</label>
                <input
                  id="inputTokens"
                  type="number"
                  min="0"
                  step="0.1"
                  value={inputs.usage.inputTokens}
                  onChange={(e) =>
                    handleUsageChange("inputTokens", e.target.value)
                  }
                  className="input-field"
                />
              </div>

              <div className="form-group">
                <label htmlFor="outputTokens">Output Tokens (millions)</label>
                <input
                  id="outputTokens"
                  type="number"
                  min="0"
                  step="0.1"
                  value={inputs.usage.outputTokens}
                  onChange={(e) =>
                    handleUsageChange("outputTokens", e.target.value)
                  }
                  className="input-field"
                />
              </div>

              <div className="form-group">
                <label htmlFor="meetingHours">
                  Meeting Hours (with transcription)
                </label>
                <input
                  id="meetingHours"
                  type="number"
                  min="0"
                  value={inputs.usage.meetingHours}
                  onChange={(e) => {
                    handleUsageChange("meetingHours", e.target.value);
                    handleUsageChange("transcriptionHours", e.target.value);
                  }}
                  className="input-field"
                />
              </div>
            </div>

            <div className="card">
              <h2>Integrations</h2>
              <div className="form-group">
                <label htmlFor="connections">Active Connections</label>
                <input
                  id="connections"
                  type="number"
                  min="0"
                  value={inputs.integrations.connections}
                  onChange={(e) =>
                    handleIntegrationChange("connections", e.target.value)
                  }
                  className="input-field"
                />
                <p className="hint">$1/connection/month</p>
              </div>

              <div className="form-group">
                <label htmlFor="records">Records Stored</label>
                <input
                  id="records"
                  type="number"
                  min="0"
                  value={inputs.integrations.records}
                  onChange={(e) =>
                    handleIntegrationChange("records", e.target.value)
                  }
                  className="input-field"
                />
                <p className="hint">$0.002/record</p>
              </div>

              <div className="form-group">
                <label htmlFor="actions">Actions Executed</label>
                <input
                  id="actions"
                  type="number"
                  min="0"
                  value={inputs.integrations.actions}
                  onChange={(e) =>
                    handleIntegrationChange("actions", e.target.value)
                  }
                  className="input-field"
                />
                <p className="hint">$0.01/action</p>
              </div>
            </div>

            <div className="card">
              <h2>Professional Services (Optional)</h2>
              <div className="form-group">
                <label htmlFor="psHours">Solutions Architect Hours</label>
                <input
                  id="psHours"
                  type="number"
                  min="0"
                  value={inputs.professionalServices.hours}
                  onChange={(e) =>
                    handleProfessionalServicesChange("hours", e.target.value)
                  }
                  className="input-field"
                />
              </div>

              <div className="form-group">
                <label htmlFor="psRate">Hourly Rate ($)</label>
                <select
                  id="psRate"
                  value={inputs.professionalServices.rate}
                  onChange={(e) =>
                    handleProfessionalServicesChange("rate", e.target.value)
                  }
                  className="input-field"
                >
                  <option value={PROFESSIONAL_SERVICES.standardRate}>
                    ${PROFESSIONAL_SERVICES.standardRate}/hr (Standard)
                  </option>
                  <option value={PROFESSIONAL_SERVICES.costRate}>
                    ${PROFESSIONAL_SERVICES.costRate}/hr (At Cost - Discounted)
                  </option>
                  <option value="0">Free (Bundled with deal)</option>
                </select>
              </div>

              {inputs.professionalServices.hours > 0 && (
                <div className="ps-packages">
                  <p className="hint">Common packages:</p>
                  <button
                    className="package-btn"
                    onClick={() =>
                      handleProfessionalServicesChange("hours", 40)
                    }
                  >
                    Implementation (40hrs)
                  </button>
                  <button
                    className="package-btn"
                    onClick={() =>
                      handleProfessionalServicesChange("hours", 20)
                    }
                  >
                    Integration (20hrs)
                  </button>
                  <button
                    className="package-btn"
                    onClick={() =>
                      handleProfessionalServicesChange("hours", 10)
                    }
                  >
                    Optimization (10hrs)
                  </button>
                </div>
              )}
            </div>

            <div className="card">
              <h2>Support Tier</h2>
              <div className="form-group">
                <label htmlFor="supportTier">Support Level</label>
                <select
                  id="supportTier"
                  value={inputs.supportTier}
                  onChange={(e) =>
                    handleInputChange("supportTier", e.target.value)
                  }
                  className="input-field"
                >
                  <option value="STANDARD">Standard Support (Included)</option>
                  <option value="ENTERPRISE">
                    Enterprise Support (30% of MRR, min $500)
                  </option>
                </select>
                <p className="hint">
                  {inputs.supportTier === "STANDARD"
                    ? "Business hours support, 24hr response time"
                    : "24/7 support, 1hr response, dedicated engineer"}
                </p>
              </div>
            </div>

            <div className="card">
              <h2>Discounts & Adjustments</h2>
              <div className="form-group">
                <label htmlFor="discount">Additional Discount (%)</label>
                <input
                  id="discount"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={inputs.discount}
                  onChange={(e) =>
                    handleInputChange(
                      "discount",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className="input-field"
                />
                {inputs.discount > 15 && (
                  <p className="hint warning">
                    ⚠ Discount exceeds 15% guideline - requires approval
                  </p>
                )}
                {inputs.discount > 20 && (
                  <p className="hint error">
                    🚫 Discount exceeds 20% - requires C-level approval
                  </p>
                )}
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={inputs.setupFeeWaived}
                    onChange={(e) =>
                      handleInputChange("setupFeeWaived", e.target.checked)
                    }
                  />
                  <span>Waive Setup Fee (${model.setupFee})</span>
                </label>
              </div>
            </div>

            <button className="calculate-btn" onClick={handleCalculate}>
              Calculate Pricing
            </button>
          </div>

          {results && (
            <div className="results-section">
              <PricingBreakdown results={results} inputs={inputs} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
